/**
 * Single switch for the SI (International) units feature: the Settings unit
 * radio, the SI unit labels on Add/Edit Test Results, and the converted numbers
 * on every report surface (analysis, comparison, charts, PDF).
 *
 * They ship together, so they read one flag rather than each repeating an env
 * check that can drift — same pattern as supplementsVisibility().
 *
 * Currently: visible everywhere EXCEPT prod.
 *
 * Fails closed. Only scripts/deploy.js sets REACT_APP_ENV, so a plain
 * `npm run build` leaves it unset — that must read as "hidden" rather than
 * "not prod", which is what a bare `!== 'prod'` would wrongly do. `npm start`
 * (NODE_ENV=development) and jest are always visible unless REACT_APP_ENV
 * explicitly names prod, which is what lets tests exercise the prod path.
 *
 * Nothing else needs gating: resolveUnitSystem() returns 'US' whenever this is
 * false, so every conversion becomes an identity and every label stays as it
 * is today.
 *
 * A function, not a const, so it re-reads the env per call; CRA still inlines
 * both process.env reads at build time.
 */
export function siUnitsVisible(): boolean {
  // ---------------------------------------------------------------------
  // ON HOLD while the feature is only half-built. Hidden EVERYWHERE, local
  // dev included, exactly as bwhLabsVisible() parks the BWH Labs brand.
  //
  // What already works: the conversion registry, the Settings radio, the
  // per-client override, the PDF sessionStorage bridge, and the display of
  // marker VALUES, unit labels and comparison-table bounds.
  //
  // What is still missing — and why turning this on now would be worse than
  // leaving it off:
  //   1. The entry form does not convert. A practitioner typing 5.5 mmol/L
  //      for an SI client would have 5.5 mg/dL stored. That corrupts data,
  //      not just the display.
  //   2. The four chart axis formatters still print canonical bounds, so an
  //      SI client's bars would carry mg/dL numbers under an mmol/L value.
  //   3. BloodworkAnalysis.defaultUnitSystem is not yet supplied by the
  //      containers, so the Settings default cannot reach a report at all.
  //
  // To launch: land those three, then restore the env-gated body below
  // (dev + slava, not prod) and remove prod's hold when it goes live.
  // ---------------------------------------------------------------------
  return false;

  // eslint-disable-next-line no-unreachable
  if (process.env.REACT_APP_ENV === 'prod') return false;
  // eslint-disable-next-line no-unreachable
  if (process.env.NODE_ENV !== 'production') return true;
  // eslint-disable-next-line no-unreachable
  return !!process.env.REACT_APP_ENV;
}
